"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { getLocaleFromPathname, withLocale } from "@/lib/i18n";
import { LanguageSwitcher } from "@/components/layout/LanguageSwitcher";
import { getDictionary } from "@/lib/messages";
import { getLayoutCopy } from "@/lib/layout-copy";

// Custom NavLink component for consistent styling and active state
const NavLink = ({ href, children, onClick }: { href: string; children: React.ReactNode; onClick?: () => void }) => {
  const pathname = usePathname();
  const locale = getLocaleFromPathname(pathname || "/");
  const localizedHref = withLocale(locale, href);
  const isActive = pathname === localizedHref || (href !== "/" && pathname?.startsWith(localizedHref));

  return (
    <Link
      href={localizedHref}
      onClick={onClick}
      className={cn(
        "px-5 py-2.5 rounded-[14px] text-base font-medium transition-all duration-200",
        isActive
          ? "bg-positivus-green text-black shadow-[0_3px_0_0_#191A23] border border-black/10 translate-y-[-2px]"
          : "text-black hover:bg-positivus-grey hover:shadow-sm"
      )}
    >
      {children}
    </Link>
  );
};

export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const pathname = usePathname() || "/";
  const locale = getLocaleFromPathname(pathname);
  const dict = getDictionary(locale);
  const layoutCopy = getLayoutCopy(locale);

  return (
    <nav className="sticky top-0 z-50 w-full bg-white/90 backdrop-blur-md border-b border-black/5">
      <div className="container mx-auto px-4 md:px-6 h-20 flex items-center justify-between">
        <Link href={withLocale(locale, "/")} className="flex items-center gap-2" onClick={() => setIsOpen(false)}>
            <img src="/logo.svg" alt="Dogufy" className="h-9 w-auto bg-positivus-dark rounded-md p-1.5" />
            <span className="text-2xl font-bold font-sans tracking-tight">
              dogufy<span className="text-positivus-green">.</span>com
            </span>
        </Link>
        
        {/* Desktop Menu */}
        <div className="hidden md:flex items-center gap-2">
          <NavLink href="/">{dict.nav.home}</NavLink>
          <NavLink href="/tools">{dict.nav.allTools}</NavLink>
          <NavLink href="/blog">{dict.nav.blog}</NavLink>
          <NavLink href="/about">{dict.nav.about}</NavLink>
          <NavLink href="/contact">{layoutCopy.footer.contact}</NavLink>
          <div className="ml-2">
            <LanguageSwitcher />
          </div>
          
          <div className="ml-4">
            <Button variant="outline" className="h-12 px-6 rounded-[14px] border-2 border-positivus-dark bg-white text-black hover:bg-positivus-green hover:text-black transition-all shadow-[0_4px_0_0_#191A23] hover:translate-y-[-2px] hover:shadow-[0_6px_0_0_#191A23]" asChild>
              <Link href={withLocale(locale, "/#tools")}>{dict.nav.exploreTools}</Link>
            </Button>
          </div>
        </div>

        {/* Mobile Menu Toggle */}
        <button 
          className="md:hidden p-2 text-positivus-dark hover:bg-positivus-grey rounded-lg transition-colors"
          onClick={() => setIsOpen(!isOpen)}
          aria-label={layoutCopy.toggleMenu}
        >
          {isOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden absolute top-20 left-0 w-full bg-white border-t border-black/5 animate-in slide-in-from-top-5 fade-in-0 duration-200 shadow-xl">
          <div className="container mx-auto px-4 py-8 flex flex-col gap-4">
            <NavLink href="/" onClick={() => setIsOpen(false)}>{dict.nav.home}</NavLink>
            <NavLink href="/tools" onClick={() => setIsOpen(false)}>{dict.nav.allTools}</NavLink>
            <NavLink href="/blog" onClick={() => setIsOpen(false)}>{dict.nav.blog}</NavLink>
            <NavLink href="/about" onClick={() => setIsOpen(false)}>{dict.nav.about}</NavLink>
            <NavLink href="/contact" onClick={() => setIsOpen(false)}>{layoutCopy.footer.contact}</NavLink>
            <LanguageSwitcher />
            
            <div className="pt-4">
              <Button className="h-14 w-full text-lg rounded-[14px] border-2 border-positivus-dark bg-positivus-dark text-white hover:bg-positivus-green hover:text-black transition-all shadow-[0_4px_0_0_#000000]" asChild onClick={() => setIsOpen(false)}>
                <Link href={withLocale(locale, "/#tools")}>{dict.nav.exploreTools}</Link>
              </Button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
